import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";

const Ctx = createContext(null);
export const useMember = () => useContext(Ctx);

export function MemberProvider({ children }) {
  const { isSignedIn } = useAuth();
  const api = useApi();
  const [me, setMe] = useState(null);
  const [favs, setFavs] = useState([]);
  const [notes, setNotes] = useState({});

  useEffect(() => {
    if (!isSignedIn) { setMe(null); setFavs([]); setNotes({}); return; }
    (async () => {
      try {
        const m = await api("get", "/me");
        setMe(m);
        const [f, n] = await Promise.all([api("get", "/favourites"), api("get", "/notes")]);
        setFavs(f.map((x) => x.horse));
        setNotes(Object.fromEntries(n.map((x) => [x.horse, x.text])));
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, [isSignedIn, api]);

  const toggleFav = useCallback(async (horse) => {
    const on = favs.includes(horse);
    setFavs((f) => (on ? f.filter((x) => x !== horse) : [horse, ...f]));
    try {
      on ? await api("delete", `/favourites/${encodeURIComponent(horse)}`) : await api("post", "/favourites", { horse });
      toast.success(on ? `Removed ${horse}` : `${horse} added to favourites`);
    } catch (e) {
      setFavs((f) => (on ? [horse, ...f] : f.filter((x) => x !== horse)));
      toast.error(errMsg(e));
    }
  }, [favs, api]);

  const saveNote = useCallback(async (horse, text) => {
    await api("put", "/notes", { horse, text });
    setNotes((n) => ({ ...n, [horse]: text }));
  }, [api]);

  const acceptTerms = useCallback(async () => {
    await api("post", "/me/accept-terms", { accepted: true, over_18: true });
    setMe((m) => ({ ...m, terms_accepted: true }));
  }, [api]);

  return (
    <Ctx.Provider value={{ me, isSignedIn, isAdmin: me?.role === "admin", favs, notes, toggleFav, saveNote, acceptTerms }}>
      {children}
    </Ctx.Provider>
  );
}
